/**
 * Symphony GROUP CHAT, Phase 5 — the members' own Maestros in a group: the public approval card, the "can't help
 * right now" notice, who may add / remove / configure which Maestro, and the owner-private settings draft.
 *
 * Pure (no React, no fetch), so web, iOS (`GroupMaestro.swift`) and Android (`GroupMaestro.kt`) can be read side by
 * side; `group-maestro.test.ts` · `GroupMaestroTests` · `GroupMaestroTest` hold the same cases. The wire contract is
 * the gateway's (`runGroupChatAction`, actions join-maestro · remove-maestro · maestro-settings ·
 * update-maestro-settings · resolve-approval).
 */
import { groupMaestroName, maestroLabel, ownerShortName, personById, viewerRole, type GroupMembersAdded, type GroupMessageMeta, type GroupParticipant, type GroupSummary, type MessageAuthor, type Translate } from './group-chat';
import type { GroupActionOutcome } from './group-chat-actions';

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string | undefined => (typeof v === 'string' && v.length > 0 ? v : undefined);

// ─── The approval card ───────────────────────────────────────────────────────────────────────────────────────

export const APPROVAL_OPERATIONS = ['READ_CALENDAR', 'READ_FILES', 'EDIT_DOCUMENT', 'SEND_MESSAGE', 'READ_PRIVATE', 'OTHER_ACTION'] as const;
export type ApprovalOperation = (typeof APPROVAL_OPERATIONS)[number];
export const APPROVAL_STATES = ['PENDING', 'APPROVED', 'DENIED', 'EXPIRED', 'EXECUTED', 'INVALIDATED'] as const;
export type ApprovalState = (typeof APPROVAL_STATES)[number];

/** `metadata.groupApproval` on a MAESTRO message. The message's `content` is ignored: the card is always localized. */
export interface GroupApproval {
  approvalId: string;
  agentOwnerMetaSiteId: string;
  requesterMetaSiteId?: string;
  operation: ApprovalOperation;
  state: ApprovalState;
  expiresDate?: string;
}

/** The card, or null (absent, no id/owner, or a missing/unknown state → render the message as a normal one). */
export function parseGroupApproval(metadata: unknown): GroupApproval | null {
  if (!isObject(metadata) || !isObject(metadata.groupApproval)) return null;
  const raw = metadata.groupApproval;
  const approvalId = str(raw.approvalId);
  const agentOwnerMetaSiteId = str(raw.agentOwnerMetaSiteId);
  const state = (APPROVAL_STATES as readonly string[]).includes(String(raw.state)) ? (raw.state as ApprovalState) : null;
  if (!approvalId || !agentOwnerMetaSiteId || !state) return null;
  const operation = (APPROVAL_OPERATIONS as readonly string[]).includes(String(raw.operation)) ? (raw.operation as ApprovalOperation) : 'OTHER_ACTION';
  const requesterMetaSiteId = str(raw.requesterMetaSiteId);
  const expiresDate = str(raw.expiresDate);
  return {
    approvalId,
    agentOwnerMetaSiteId,
    ...(requesterMetaSiteId ? { requesterMetaSiteId } : {}),
    operation,
    state,
    ...(expiresDate ? { expiresDate } : {}),
  };
}

type ApprovalRow = { id?: string; finalId?: string; groupApproval?: GroupApproval | null };
const rowId = (m: ApprovalRow) => m.finalId ?? m.id;

/** approvalId → the id of its LAST card in transcript order. Every earlier card of that approval is superseded. */
export function latestApprovalMessageIds(messages: ApprovalRow[]): Map<string, string> {
  const latest = new Map<string, string>();
  for (const m of messages) {
    const id = rowId(m);
    if (m.groupApproval && id) latest.set(m.groupApproval.approvalId, id);
  }
  return latest;
}

/** Whether this row is an approval card that a newer card of the same approval replaced (not rendered). */
export function isSupersededApproval(message: ApprovalRow, latest: Map<string, string>): boolean {
  if (!message.groupApproval) return false;
  const id = rowId(message);
  return !!id && latest.get(message.groupApproval.approvalId) !== id;
}

/** A deep link / push at a superseded card lands on the latest card of the same approval; anything else as asked. */
export function approvalScrollTarget(messages: ApprovalRow[], targetMessageId: string | null | undefined): string | null {
  if (!targetMessageId) return null;
  const target = messages.find((m) => m.id === targetMessageId || m.finalId === targetMessageId);
  if (!target?.groupApproval) return targetMessageId;
  return latestApprovalMessageIds(messages).get(target.groupApproval.approvalId) ?? targetMessageId;
}

/** What this client decided about a card after answering it (until the newer card arrives). */
export interface ApprovalLocal {
  /** A state to show instead of the card's own. */
  state?: ApprovalState;
  /** The buttons are gone for good (any final answer). */
  resolved?: boolean;
  /** A call is in flight. */
  busy?: boolean;
}

export interface ApprovalCardView {
  state: ApprovalState;
  showButtons: boolean;
  busy: boolean;
}

/**
 * What the card draws for this viewer. Only the Maestro's OWNER ever sees Approve / Deny, and only while it is
 * PENDING and not past `expiresDate`; a local answer overrides the card's state; an in-flight call disables both.
 */
export function approvalCardView(card: GroupApproval, viewerId: string | null | undefined, nowMs: number, local?: ApprovalLocal | null): ApprovalCardView {
  const expired = card.state === 'PENDING' && !!card.expiresDate && Number.isFinite(Date.parse(card.expiresDate)) && Date.parse(card.expiresDate) < nowMs;
  const state = local?.state ?? (expired ? 'EXPIRED' : card.state);
  const owner = !!viewerId && viewerId === card.agentOwnerMetaSiteId;
  return { state, showButtons: owner && state === 'PENDING' && !local?.resolved, busy: !!local?.busy };
}

/** The local state and the message (null: none) an answer to `resolve-approval` leaves behind. */
export interface ApprovalOutcome {
  local: ApprovalLocal;
  messageKey: string | null;
}
export function resolveOutcome(outcome: GroupActionOutcome<unknown>): ApprovalOutcome {
  if (outcome.kind === 'ok') {
    const approved = isObject(outcome.data) && outcome.data.approved === true;
    return { local: { state: approved ? 'APPROVED' : 'DENIED', resolved: true }, messageKey: null };
  }
  if (outcome.kind === 'refused') {
    switch (outcome.refusal) {
      case 'APPROVAL_EXPIRED': return { local: { state: 'EXPIRED', resolved: true }, messageKey: 'chat.group.approval.error.expired' };
      case 'APPROVAL_INVALIDATED': return { local: { state: 'INVALIDATED', resolved: true }, messageKey: 'chat.group.approval.error.invalidated' };
      // Someone (another device) already answered: no buttons, the card keeps its chip until the newer card lands.
      case 'APPROVAL_ALREADY_RESOLVED': return { local: { resolved: true }, messageKey: 'chat.group.approval.error.alreadyResolved' };
      case 'APPROVAL_NOT_FOUND':
      case 'NOT_MAESTRO_OWNER': return { local: { resolved: true }, messageKey: 'chat.group.approval.error.notFound' };
      case 'FEATURE_OFF': return { local: {}, messageKey: 'chat.group.error.featureOff' };
      default: return { local: {}, messageKey: 'chat.group.error.generic' };
    }
  }
  return { local: {}, messageKey: 'chat.group.error.generic' };
}

export const APPROVAL_OPERATION_KEYS: Record<ApprovalOperation, string> = {
  READ_CALENDAR: 'chat.group.approval.op.readCalendar',
  READ_FILES: 'chat.group.approval.op.readFiles',
  EDIT_DOCUMENT: 'chat.group.approval.op.editDocument',
  SEND_MESSAGE: 'chat.group.approval.op.sendMessage',
  READ_PRIVATE: 'chat.group.approval.op.readPrivate',
  OTHER_ACTION: 'chat.group.approval.op.otherAction',
};
export const APPROVAL_STATE_KEYS: Record<ApprovalState, string> = {
  PENDING: 'chat.group.approval.state.pending',
  APPROVED: 'chat.group.approval.state.approved',
  DENIED: 'chat.group.approval.state.denied',
  EXPIRED: 'chat.group.approval.state.expired',
  EXECUTED: 'chat.group.approval.state.executed',
  INVALIDATED: 'chat.group.approval.state.invalidated',
};

/** The card's words: title, "Asked by …" (null when the requester is unknown) and "Needs: …". Never a protected value. */
export function approvalCardText(card: GroupApproval, summary: GroupSummary, t: Translate): { title: string; askedBy: string | null; needs: string } {
  const maestro = summary.participants.find((p) => p.kind === 'MAESTRO' && p.ownerId === card.agentOwnerMetaSiteId);
  const agentName = maestro?.displayName || t('home.map.maestro');
  const human = (id: string | undefined) => personById(summary, id);
  // The owner's own row names them (`ownerShortName`: "Group owner", never "Group"), else the Maestro's wire name.
  const ownerHuman = human(card.agentOwnerMetaSiteId);
  const ownerName = ownerHuman ? ownerShortName(ownerHuman, t) : maestro?.ownerFirstName || '';
  const title = summary.viewerId && summary.viewerId === card.agentOwnerMetaSiteId
    ? t('chat.group.approval.titleYou', { agentName })
    : ownerName
      ? t('chat.group.approval.title', { agentName, owner: ownerName })
      : t('chat.group.approval.titleGeneric', { agentName });
  const requester = card.requesterMetaSiteId;
  const requesterName = requester && requester === summary.viewerId
    ? t('chat.group.you')
    : human(requester) ? ownerShortName(human(requester)!, t) : '';
  return {
    title,
    askedBy: requesterName ? t('chat.group.approval.askedBy', { name: requesterName }) : null,
    needs: t('chat.group.approval.needs', { operation: t(APPROVAL_OPERATION_KEYS[card.operation]) }),
  };
}

// ─── "Can't help right now" ──────────────────────────────────────────────────────────────────────────────────

export const UNAVAILABLE_REASONS = ['OUT_OF_CREDITS', 'DAILY_CAP', 'ASKER_LIMIT', 'GROUP_LIMIT', 'PAUSED'] as const;
export type UnavailableReason = (typeof UNAVAILABLE_REASONS)[number] | 'UNKNOWN';

/** `metadata.maestroUnavailable.reason`, "UNKNOWN" when present but unrecognized, null when there is no notice. */
export function parseMaestroUnavailable(metadata: unknown): UnavailableReason | null {
  if (!isObject(metadata) || !isObject(metadata.maestroUnavailable)) return null;
  const reason = metadata.maestroUnavailable.reason;
  return (UNAVAILABLE_REASONS as readonly string[]).includes(String(reason)) ? (reason as UnavailableReason) : 'UNKNOWN';
}

const UNAVAILABLE_KEYS: Record<UnavailableReason, string> = {
  OUT_OF_CREDITS: 'chat.group.unavailable.outOfCredits',
  DAILY_CAP: 'chat.group.unavailable.dailyCap',
  ASKER_LIMIT: 'chat.group.unavailable.askerLimit',
  GROUP_LIMIT: 'chat.group.unavailable.groupLimit',
  PAUSED: 'chat.group.unavailable.paused',
  UNKNOWN: 'chat.group.unavailable.generic',
};
/** The notice line — never a balance or a number. */
export function unavailableLine(reason: string, maestroName: string, t: Translate): string {
  const key = UNAVAILABLE_KEYS[reason as UnavailableReason] ?? UNAVAILABLE_KEYS.UNKNOWN;
  return t(key, { name: maestroName });
}

/** The Maestro a notice or card names, from its author: kind-aware (`resolveAuthor` rules), by owner. */
export function maestroForAuthor(summary: GroupSummary, author: MessageAuthor | null | undefined): GroupParticipant | null {
  if (!author || author.kind !== 'MAESTRO') return null;
  const owner = author.agentOwnerMetaSiteId ?? author.principalMetaSiteId;
  return summary.participants.find((p) => p.kind === 'MAESTRO' && (p.id === author.principalMetaSiteId || p.ownerId === owner)) ?? null;
}

// ─── Who may do what ─────────────────────────────────────────────────────────────────────────────────────────

export function maestroRows(summary: GroupSummary): GroupParticipant[] {
  return summary.participants.filter((p) => p.kind === 'MAESTRO');
}
export function myMaestro(summary: GroupSummary): GroupParticipant | null {
  if (!summary.viewerId) return null;
  return maestroRows(summary).find((p) => p.ownerId === summary.viewerId) ?? null;
}
/** Its owner, or a group OWNER / ADMIN. */
export function canRemoveMaestro(summary: GroupSummary, maestro: GroupParticipant): boolean {
  if (maestro.kind !== 'MAESTRO') return false;
  if (summary.viewerId && maestro.ownerId === summary.viewerId) return true;
  const role = viewerRole(summary);
  return role === 'OWNER' || role === 'ADMIN';
}
/** Phase 8: who may stop a Maestro's work here — the same people who may remove it (its owner, a group owner or admin). */
export function canStopMaestro(summary: GroupSummary, maestro: GroupParticipant): boolean {
  if (maestro.kind !== 'MAESTRO') return false;
  if (summary.viewerId && maestro.ownerId === summary.viewerId) return true;
  const role = viewerRole(summary);
  return role === 'OWNER' || role === 'ADMIN';
}

/** Tells an open transcript that a Maestro was stopped from elsewhere (the participant sheet), so its working line reads stopped. */
export const GROUP_MAESTRO_STOPPED_EVENT = 'symphony:group-maestro-stopped';
export interface GroupMaestroStoppedDetail {
  conversationId: string;
  ownerId: string;
}

/** A `stop-maestro-work` answer: stopped, or the message to show (NOT_MAESTRO_OWNER names who may; anything else "couldn't stop"). */
export type StopOutcome = { kind: 'stopped' } | { kind: 'error'; messageKey: string };
export function stopOutcome(outcome: GroupActionOutcome<unknown>): StopOutcome {
  if (outcome.kind === 'ok') return { kind: 'stopped' };
  if (outcome.kind === 'refused' && outcome.refusal === 'NOT_MAESTRO_OWNER') return { kind: 'error', messageKey: 'chat.group.error.notMaestroOwner' };
  return { kind: 'error', messageKey: 'chat.group.maestro.stopFailed' };
}

/**
 * What a participant-sheet Stop left under THAT Maestro's row (iOS `GroupMaestro.StopRowLine`, Android
 * `MaestroStopRowLine`): "{name} stopped", or why it could not stop. `messageKey` takes `{maestroAgentName}`.
 */
export interface StopRowLine {
  ownerId: string;
  messageKey: string;
  isError: boolean;
}
export function stopRowLine(ownerId: string, result: StopOutcome): StopRowLine {
  return result.kind === 'stopped'
    ? { ownerId, messageKey: 'chat.group.maestro.stopped', isError: false }
    : { ownerId, messageKey: result.messageKey, isError: true };
}
/** `line` when it belongs under the Maestro row of `ownerId`; one Maestro's outcome never shows under another. */
export function stopRowLineFor(line: StopRowLine | null, ownerId: string | null | undefined): StopRowLine | null {
  return line && ownerId && line.ownerId === ownerId ? line : null;
}

/**
 * Phase 8 SYSTEM lines, always localized: "{name} was stopped…" (the Maestro named from the summary; a Maestro that
 * has left falls back to the row's own text, else the default name) and "Paused here…" for a chain at a bound.
 */
export function groupSystemLine(
  meta: Pick<GroupMessageMeta, 'workStopped' | 'chainStopped' | 'membersAdded'> | null | undefined,
  content: string | null | undefined,
  summary: GroupSummary,
  t: Translate,
  fallbackName: string,
  locale?: string,
): string | null {
  if (meta?.workStopped) {
    const owner = meta.workStopped.maestroOwnerMetaSiteId;
    const maestro = summary.participants.find((p) => p.kind === 'MAESTRO' && p.ownerId === owner);
    const label = maestro ? maestroLabel(maestro, t).trim() : '';
    // From the metadata only: a Maestro that has left (or has no name) reads as the default name, never the row's text.
    return t('chat.group.event.workStopped', { maestroAgentName: label || fallbackName });
  }
  if (meta?.chainStopped) return t('chat.group.event.chainStopped');
  if (meta?.membersAdded) return membersAddedLine(meta.membersAdded, content, summary, t, locale);
  return null;
}

/** "Caro", "Caro and Alex", "Caro, Alex and Dana" in `locale` (iOS / Android `naturalList`); ", " where Intl cannot. */
export function naturalList(items: readonly string[], locale = 'en'): string {
  if (items.length <= 1) return items[0] ?? '';
  try {
    return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(items);
  } catch {
    return items.join(', ');
  }
}

/**
 * R5 SYSTEM line, localized from ids (iOS `GroupMaestro.membersAddedLine`, Android `membersAddedLine`): "You added
 * {names}" for the adder, "{actor} added you" for an added viewer, "{actor} added {names}" for everyone else. Names are
 * first names from the participant summary (a nameless person's WHOLE role fallback), joined as a natural list ("Caro
 * and Alex"); an actor, or a set of added people, nobody can name reads "Someone" — never the row's own (English) text.
 * The chat line and the chats-list preview of a live MEMBERS_ADDED row both draw this. `content` is no longer read.
 */
export function membersAddedLine(event: GroupMembersAdded, _content: string | null | undefined, summary: GroupSummary, t: Translate, locale?: string): string {
  const viewer = summary.viewerId;
  const someone = t('chat.group.someone');
  const human = (id: string) => personById(summary, id);
  const short = (id: string) => {
    const p = human(id);
    return p ? ownerShortName(p, t) : '';
  };
  const names = naturalList(event.memberMetaSiteIds.filter((id) => id !== viewer).map(short).filter(Boolean), locale) || someone;
  const actor = short(event.actorMetaSiteId) || someone;
  if (viewer && event.actorMetaSiteId === viewer) return t('chat.group.event.youAdded', { names });
  if (viewer && event.memberMetaSiteIds.includes(viewer)) return t('chat.group.event.addedYou', { actor });
  return t('chat.group.event.membersAdded', { actor, names });
}

/** One run of a message's text: plain, or an `@Name` mention of a Maestro. */
export interface MentionSegment {
  text: string;
  mention: boolean;
}
const isWordChar = (c: string | undefined) => !!c && /[\p{L}\p{N}_]/u.test(c);
/**
 * Splits `text` on `@Name` tokens for the given names: case-insensitive, the longest name first, only at a word start
 * (so an e-mail address is not a mention) and only where the name ends a word.
 */
export function mentionSegments(text: string, names: string[]): MentionSegment[] {
  const wanted = [...new Set(names.filter((n) => n.trim().length > 0))].sort((a, b) => b.length - a.length);
  const out: MentionSegment[] = [];
  let plain = '';
  let i = 0;
  const lower = text.toLowerCase();
  while (i < text.length) {
    if (text[i] === '@' && !isWordChar(text[i - 1])) {
      const hit = wanted.find((n) => lower.startsWith(n.toLowerCase(), i + 1) && !isWordChar(text[i + 1 + n.length]));
      if (hit) {
        if (plain) out.push({ text: plain, mention: false });
        plain = '';
        out.push({ text: text.slice(i, i + 1 + hit.length), mention: true });
        i += 1 + hit.length;
        continue;
      }
    }
    plain += text[i];
    i += 1;
  }
  if (plain) out.push({ text: plain, mention: false });
  return out;
}

/**
 * Every name a group `@` mention can carry — each participant's display name, the viewer's own (so a mention of you is
 * drawn on your screen) and every Maestro's — what a posted bubble highlights on every member's screen. The `@` picker
 * inserts exactly these names (`groupMentionSections`). iOS / Android `mentionTargetNames` list the same.
 */
export function groupMentionNames(summary: GroupSummary): string[] {
  // Former members too: an `@Netta` written while she was in the group still reads as a mention once she is gone.
  return [...new Set([...summary.participants, ...(summary.formerParticipants ?? [])].map((p) => p.displayName.trim()).filter(Boolean))];
}

/**
 * A Maestro's reply that asks other Maestros: the names to draw as mentions (other Maestros in the group, by
 * displayName), and the ones its text never wrote as `@Name`, which the bubble prefixes so the ask reads naturally.
 */
export function maestroAskMentions(
  summary: GroupSummary,
  author: MessageAuthor | null | undefined,
  maestroMentions: string[] | undefined,
  text: string,
): { names: string[]; missing: string[] } {
  if (!author || author.kind !== 'MAESTRO' || !maestroMentions?.length) return { names: [], missing: [] };
  const self = author.agentOwnerMetaSiteId ?? author.principalMetaSiteId;
  const names: string[] = [];
  for (const owner of maestroMentions) {
    if (owner === self) continue;
    const p = summary.participants.find((x) => x.kind === 'MAESTRO' && x.ownerId === owner);
    if (p?.displayName && !names.includes(p.displayName)) names.push(p.displayName);
  }
  const present = new Set(mentionSegments(text, names).filter((s) => s.mention).map((s) => s.text.slice(1).toLowerCase()));
  return { names, missing: names.filter((n) => !present.has(n.toLowerCase())) };
}

/**
 * The Markdown a Maestro's ask renders: the missing `@Name`s prefixed, then every `@Name` of the asked Maestros drawn
 * as a mention (bold). Text with no names is returned untouched.
 */
export function mentionMarkdown(text: string, names: string[], missing: string[]): string {
  if (names.length === 0) return text;
  const withPrefix = missing.length ? `${missing.map((n) => `@${n}`).join(' ')} ${text}` : text;
  return mentionSegments(withPrefix, names)
    .map((s) => (s.mention ? `**${s.text}**` : s.text))
    .join('');
}

export function canAddMyMaestro(summary: GroupSummary, enabled: boolean): boolean {
  return enabled && !!summary.viewerId && myMaestro(summary) === null;
}
export function canOpenMaestroSettings(summary: GroupSummary): boolean {
  return myMaestro(summary) !== null;
}

// ─── Settings ────────────────────────────────────────────────────────────────────────────────────────────────

export const GRANT_KINDS = ['CALENDAR_AVAILABILITY', 'FILES_READ', 'DOCUMENT_EDIT'] as const;
export type GrantKind = (typeof GRANT_KINDS)[number];
export const MAX_GRANT_PATHS = 20;
export const MAX_GRANTS = 20;
export const MAX_PATH_LENGTH = 500;
export const LIMIT_STEP = 5;
export const LIMIT_MIN = 5;

export interface MaestroGrant {
  kind: GrantKind;
  paths: string[];
}
export interface MaestroSettings {
  joined: boolean;
  grants: MaestroGrant[];
  perAskerDailyTurns: number;
  groupDailyTurns: number;
  paused: boolean;
  revision: string;
  defaultPerAskerDailyTurns: number;
  defaultGroupDailyTurns: number;
  maxPerAskerDailyTurns: number;
  maxGroupDailyTurns: number;
  /** Phase 8: answers, without being mentioned, a post it judges it can help with. Saves at once, like `paused`. */
  proactive: boolean;
  /** Phase 8, read only: when its public work here was last stopped. */
  workStoppedDate: string | null;
}

const posInt = (v: unknown, fallback: number) => (typeof v === 'number' && Number.isInteger(v) && v > 0 ? v : fallback);

/** The gateway's `settings`, or null. Unknown grant kinds are ignored; one grant per kind (the first wins). */
export function parseMaestroSettings(raw: unknown): MaestroSettings | null {
  if (!isObject(raw)) return null;
  const grants: MaestroGrant[] = [];
  for (const g of Array.isArray(raw.grants) ? raw.grants : []) {
    if (!isObject(g) || !(GRANT_KINDS as readonly string[]).includes(String(g.kind))) continue;
    const kind = g.kind as GrantKind;
    if (grants.some((x) => x.kind === kind)) continue;
    const paths = kind === 'CALENDAR_AVAILABILITY' ? [] : (Array.isArray(g.paths) ? g.paths : []).filter((p): p is string => typeof p === 'string' && p.length > 0);
    grants.push({ kind, paths });
  }
  const defaultPerAsker = posInt(raw.defaultPerAskerDailyTurns, 20);
  const defaultGroup = posInt(raw.defaultGroupDailyTurns, 60);
  return {
    joined: raw.joined === true,
    grants,
    perAskerDailyTurns: posInt(raw.perAskerDailyTurns, defaultPerAsker),
    groupDailyTurns: posInt(raw.groupDailyTurns, defaultGroup),
    paused: raw.paused === true,
    revision: typeof raw.revision === 'string' || typeof raw.revision === 'number' ? String(raw.revision) : '0',
    defaultPerAskerDailyTurns: defaultPerAsker,
    defaultGroupDailyTurns: defaultGroup,
    maxPerAskerDailyTurns: posInt(raw.maxPerAskerDailyTurns, 200),
    maxGroupDailyTurns: posInt(raw.maxGroupDailyTurns, 600),
    proactive: raw.proactive === true,
    workStoppedDate: typeof raw.workStoppedDate === 'string' && raw.workStoppedDate ? raw.workStoppedDate : null,
  };
}

/** One −/+ press: snaps to the step grid, then moves one step, within [min, max]. */
export function stepLimit(value: number, up: boolean, max: number, step = LIMIT_STEP, min = LIMIT_MIN): number {
  if (up) return Math.min(max, Math.floor(value / step) * step + step);
  return Math.max(min, Math.ceil(value / step) * step - step);
}
export function canStep(value: number, up: boolean, max: number, min = LIMIT_MIN): boolean {
  return up ? value < max : value > min;
}

export function grantFor(settings: Pick<MaestroSettings, 'grants'>, kind: GrantKind): MaestroGrant | null {
  return settings.grants.find((g) => g.kind === kind) ?? null;
}

/** Calendar availability on / off. */
export function setCalendarGrant<S extends Pick<MaestroSettings, 'grants'>>(draft: S, on: boolean): S {
  const rest = draft.grants.filter((g) => g.kind !== 'CALENDAR_AVAILABILITY');
  return { ...draft, grants: on ? [{ kind: 'CALENDAR_AVAILABILITY', paths: [] }, ...rest] : rest };
}

/** Adds a chosen file or folder to a files grant: trimmed, deduped, at most 20 (a 21st is not added). */
export function addGrantPath<S extends Pick<MaestroSettings, 'grants'>>(draft: S, kind: Exclude<GrantKind, 'CALENDAR_AVAILABILITY'>, path: string): S {
  const p = path.trim();
  if (!p || p.length > MAX_PATH_LENGTH) return draft;
  const existing = grantFor(draft, kind);
  if (existing?.paths.includes(p) || (existing?.paths.length ?? 0) >= MAX_GRANT_PATHS) return draft;
  const next = { kind, paths: [...(existing?.paths ?? []), p] };
  return { ...draft, grants: existing ? draft.grants.map((g) => (g.kind === kind ? next : g)) : [...draft.grants, next] };
}
export function removeGrantPath<S extends Pick<MaestroSettings, 'grants'>>(draft: S, kind: GrantKind, path: string): S {
  return { ...draft, grants: draft.grants.map((g) => (g.kind === kind ? { ...g, paths: g.paths.filter((x) => x !== path) } : g)) };
}

/** The error key that blocks a save, or null. */
export function validateSettings(draft: Pick<MaestroSettings, 'grants'>): string | null {
  return draft.grants.some((g) => new Set(g.paths).size > MAX_GRANT_PATHS) ? 'chat.group.maestro.grant.tooMany' : null;
}

/**
 * The `update-maestro-settings` `settings`: the WHOLE settings with the last-read revision. One grant per kind (first
 * wins), paths trimmed and deduped (at most 20), a files grant with no paths DROPPED, calendar sent with no paths.
 */
export function settingsUpdateBody(draft: MaestroSettings): Record<string, unknown> {
  const grants: MaestroGrant[] = [];
  for (const g of draft.grants) {
    if (grants.some((x) => x.kind === g.kind)) continue;
    if (g.kind === 'CALENDAR_AVAILABILITY') {
      grants.push({ kind: g.kind, paths: [] });
      continue;
    }
    const paths = [...new Set(g.paths.map((p) => p.trim()).filter((p) => p && p.length <= MAX_PATH_LENGTH))].slice(0, MAX_GRANT_PATHS);
    if (paths.length > 0) grants.push({ kind: g.kind, paths });
  }
  return {
    grants: grants.slice(0, MAX_GRANTS),
    perAskerDailyTurns: Math.min(draft.maxPerAskerDailyTurns, Math.max(1, Math.round(draft.perAskerDailyTurns))),
    groupDailyTurns: Math.min(draft.maxGroupDailyTurns, Math.max(1, Math.round(draft.groupDailyTurns))),
    paused: draft.paused,
    proactive: draft.proactive,
    revision: draft.revision,
  };
}

/**
 * Phase 8: whether the "Reply without being mentioned" switch may move to `next`. Off is always allowed; on needs
 * the groupChat toggle (the service refuses it otherwise).
 */
export function proactiveChangeAllowed(next: boolean, groupChatEnabled: boolean): boolean {
  return !next || groupChatEnabled;
}

/** Whether the Save button has anything to save: limits or grants differ from what was loaded. */
export function settingsDirty(loaded: MaestroSettings, draft: MaestroSettings): boolean {
  const norm = (s: MaestroSettings) => JSON.stringify({ ...settingsUpdateBody(s), paused: false, proactive: false, revision: '' });
  return norm(loaded) !== norm(draft);
}

/**
 * The settings screen's state machine for one save answer: ok → the saved settings (`saved` message);
 * STALE_SETTINGS → reload (the draft is discarded) with `staleSettings`; anything else → keep the draft, show the
 * mapped error (FEATURE_OFF → `featureOff`).
 */
export type SaveResult =
  | { kind: 'saved'; settings: MaestroSettings; messageKey: 'chat.group.maestro.saved' }
  | { kind: 'reload'; messageKey: 'chat.group.maestro.staleSettings' }
  | { kind: 'error'; messageKey: string };
export function saveResult(outcome: GroupActionOutcome<unknown>, refusalKeyOf: (refusal: string | null) => string): SaveResult {
  if (outcome.kind === 'ok') {
    const settings = isObject(outcome.data) ? parseMaestroSettings(outcome.data.settings) : null;
    return settings ? { kind: 'saved', settings, messageKey: 'chat.group.maestro.saved' } : { kind: 'error', messageKey: refusalKeyOf(null) };
  }
  if (outcome.kind === 'refused' && outcome.refusal === 'STALE_SETTINGS') return { kind: 'reload', messageKey: 'chat.group.maestro.staleSettings' };
  return { kind: 'error', messageKey: refusalKeyOf(outcome.kind === 'refused' ? outcome.refusal : null) };
}

/** The last path segment, as a chosen file's or folder's name. */
export function grantPathName(path: string): string {
  const trimmed = path.replace(/\/+$/, '');
  return trimmed.slice(trimmed.lastIndexOf('/') + 1) || trimmed;
}

// ─── Grant paths: the explorer's key ↔ the harness's VFS path ───────────────────────────────────────────────

/**
 * A Space as the grant paths need it. The harness matches grant paths against its agent VFS, where a Space's files
 * live at `spaces/<slug>/<rest>`; the explorer addresses the same file by its storage key `<workspaceId>/<rest>`
 * (lib/vfs-attachment.ts has the same two views of one file).
 */
export interface GrantSpace {
  id: string;
  slug: string;
  name?: string;
}
const GUID_HEAD = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A path picked in the explorer → the path a grant sends: `<workspaceId>/<rest>` → `spaces/<slug>/<rest>`; a
 * top-level non-Space path (`brain/…`, `skills/…`) unchanged. Null when it cannot be converted (an unknown
 * workspace id, or a Space with no slug) — such a pick is not added.
 */
export function grantPathFromExplorer(path: string, spaces: GrantSpace[]): string | null {
  const segments = path.trim().split('/').filter(Boolean);
  if (segments.length === 0) return null;
  const [head, ...rest] = segments;
  const space = spaces.find((s) => s.id === head);
  if (space) return space.slug ? ['spaces', space.slug, ...rest].join('/') : null;
  if (GUID_HEAD.test(head)) return null;
  return segments.join('/');
}

/** A stored grant path as a row draws it: its last segment, and `<Space name> / <rest>` (else the raw path). */
export function grantPathDisplay(path: string, spaces: GrantSpace[]): { name: string; secondary: string } {
  const name = grantPathName(path);
  const segments = path.split('/').filter(Boolean);
  if (segments[0] === 'spaces' && segments.length >= 2) {
    const space = spaces.find((s) => s.slug === segments[1]);
    if (space?.name) return { name, secondary: [space.name, ...segments.slice(2)].join(' / ') };
  }
  return { name, secondary: path };
}

// ─── Composer ────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * The composer's placeholder in a live group; null keeps a private chat's own. In a group a Maestro answers only when
 * it is @mentioned, and nothing else on screen says so (2026-11-02 feedback: "how do I make the Maestro answer?"), so
 * while the viewer's OWN Maestro is in the group (`myMaestro` — the summary holds active participants only, so a
 * removed one is gone) it reads "Message, or type @ to ask {name}", `{name}` being that Maestro as this group names
 * it (`groupMaestroName`). Otherwise "Message the group". iOS / Android `GroupMaestro.composerPlaceholder` decide the
 * same.
 *
 * The group's OWNER is the exception (2026-11-02, Ronny's decision): the owner's Maestro reads every message by default
 * and answers its owner's requests without an @ (the server default of its proactive switch), so telling the owner to
 * type @ is wrong; they read "Message the group, or just ask {name}". Members' Maestros stay mention-only until their
 * owner turns the switch on, so members keep the @ hint. The summary carries no proactive state, so an owner who
 * turned the switch OFF still reads the owner hint (@ still works for them).
 */
export function composerPlaceholder(
  groupPosting: boolean,
  summary: GroupSummary | null | undefined,
  ownAgentName: string,
  t: Translate,
  genericNames: readonly string[] = [],
): { key: string; params?: { name: string } } | null {
  if (!groupPosting) return null;
  if (!summary || summary.ended || !myMaestro(summary)) return { key: 'chat.group.composerPlaceholder' };
  const name = groupMaestroName(summary, ownAgentName, t, genericNames);
  const owner = viewerRole(summary) === 'OWNER' || (!!summary.ownerMetaSiteId && summary.ownerMetaSiteId === summary.viewerId);
  return { key: owner ? 'chat.group.composerPlaceholderOwner' : 'chat.group.composerPlaceholderAsk', params: { name } };
}

// ─── "Add my Maestro" ────────────────────────────────────────────────────────────────────────────────────────

/** The unrenamed assistant's full name and its older English form (`DEFAULT_ASSISTANT_NAME` / `LEGACY_DEFAULT_ASSISTANT_NAME` in lib/agent-name-context.ts — not imported here, so this stays import-light). */
const DEFAULT_AGENT_FULL_NAMES = ['Maestro', 'The Maestro'];

/**
 * The name "Add {maestroAgentName} to this group?" (`chat.group.maestro.addTitle` / `addBody`) gives the viewer's own
 * Maestro — the name the GROUP will know it by: the viewer's agent name when they renamed it, else "{owner}'s Maestro"
 * after the viewer (`ownerShortName`, "Caro's Maestro"). No viewer row known → the agent's short name. iOS / Android
 * `GroupMaestro.ownMaestroGroupName` decide the same.
 */
export function ownMaestroGroupName(agent: { name: string; short: string }, summary: GroupSummary | null | undefined, t: Translate): string {
  if (agent.name.trim() && !DEFAULT_AGENT_FULL_NAMES.includes(agent.name.trim())) return agent.short || agent.name;
  const me = summary?.viewerId ? summary.participants.find((p) => p.kind === 'HUMAN' && p.id === summary.viewerId) : undefined;
  if (!me) return agent.short || agent.name;
  return t('chat.group.maestroOwnerName', { ownerFirstName: ownerShortName({ displayName: me.displayName, role: viewerRole(summary!) ?? me.role }, t) });
}

/**
 * The chats-list preview of a group whose NEWEST row is a live MEMBERS_ADDED line: the same client rendering as the
 * line in the chat (`membersAddedLine`) — never the server's "Marco added Caro, Alex." text. Null when the newest
 * row is something else, or older than the row's own last activity (the list then has a newer preview of its own).
 */
export function membersAddedPreview(
  lastLive: { createdDate?: string; groupMeta?: Pick<GroupMessageMeta, 'membersAdded'> | null } | null | undefined,
  rowUpdatedDate: string | null | undefined,
  summary: GroupSummary,
  t: Translate,
  locale?: string,
): string | null {
  const event = lastLive?.groupMeta?.membersAdded;
  if (!event) return null;
  const live = lastLive?.createdDate ? Date.parse(lastLive.createdDate) : NaN;
  const updated = rowUpdatedDate ? Date.parse(rowUpdatedDate) : NaN;
  // A few seconds of slack: the row's `updatedDate` is stamped by the same write, a moment after the message's.
  if (Number.isFinite(live) && Number.isFinite(updated) && updated - live > 5_000) return null;
  return membersAddedLine(event, null, summary, t, locale);
}
